import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useState, useEffect } from "react";
import { db, auth } from "./services/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { reload } from "firebase/auth";
import Fooldal from "./pages/Fooldal";
import Naplo from "./pages/Naplo";
import Eszkozok from "./pages/Eszkozok";
import Profil from "./pages/Profil";
import AuthPage from "./pages/AuthPage";
import Onboarding from "./pages/Onboarding";
import Statisztika from "./pages/Statisztika";
import AiChat from "./pages/AiChat";
import { Home, BookOpen, BarChart2, Wrench, User, Mail } from "lucide-react";
import { ROUTE_TITLES } from "./services/analytics";
import { usePageTracking } from "./hooks/usePageTracking";
import { resolveAuthActionMode } from "./utils/authActionMode";
import { shouldShowAuthLoadingSpinner } from "./utils/authGate";
import ResetPasswordConfirm from "./pages/ResetPasswordConfirm";
import { LegalGateModal, LegalRejectedScreen } from "./components/LegalGateModal";
import { LegalBanner } from "./components/LegalBanner";
import {
  resolveLegalGate,
  ackKey,
  type LegalConfig,
  type LegalGateItem,
} from "./utils/legalGate";
import { localDateString } from "./utils/entryDate";
import { loadLegalConfig, loadAcks, writeAcks } from "./services/legalAcks";

// A /eszkozok és /signup címét/page_view-ját a saját komponensük küldi
// (előbbi az aktív kalkulátortól függ, utóbbi az AuthPage mode state-jétől),
// hogy ne duplázódjon a page_view egy-egy navigáláskor.
const ROUTER_OWNED_TITLES: Record<string, string> = {
  "/fooldal": ROUTE_TITLES["/fooldal"],
  "/naplo": ROUTE_TITLES["/naplo"],
  "/statisztika": ROUTE_TITLES["/statisztika"],
  "/profil": ROUTE_TITLES["/profil"],
};

function RouteAnalytics() {
  const location = useLocation();
  usePageTracking(ROUTER_OWNED_TITLES[location.pathname] ?? null, location.pathname);
  return null;
}

function EmailVerificationScreen({ email }: { email: string }) {
  const { signOut, resendVerification } = useAuth();
  const [resent, setResent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCheckVerified = async () => {
    setChecking(true);
    setError(null);
    try {
      if (auth.currentUser) {
        await reload(auth.currentUser);
        if (!auth.currentUser.emailVerified) {
          setError("Az email még nincs megerősítve. Kérlek kattints a linkre az emailben.");
        } else {
          window.location.reload();
        }
      }
    } catch {
      setError("Hiba történt. Kérlek próbáld újra.");
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      await resendVerification();
      setResent(true);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-sm w-full bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4 text-center">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
          <Mail size={28} className="text-emerald-700" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Erősítsd meg az emailed</h1>
          <p className="text-sm text-gray-500 mt-2">
            Küldtünk egy megerősítő emailt a <strong>{email}</strong> címre. Kattints a linkre a folytatáshoz.
          </p>
        </div>
        {resent && (
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
            <p className="text-xs text-emerald-700">✅ Email újraküldve!</p>
          </div>
        )}
        <button
          onClick={handleResend}
          disabled={loading || resent}
          className="w-full bg-emerald-700 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-emerald-800 transition-colors disabled:opacity-60"
        >
          {loading ? "Küldés..." : resent ? "Email elküldve" : "Email újraküldése"}
        </button>
        {error && (
          <div className="bg-red-50 border border-red-100 rounded-xl px-3 py-2">
            <p className="text-xs text-red-600">{error}</p>
          </div>
        )}
        <button
          onClick={handleCheckVerified}
          disabled={checking}
          className="w-full border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors disabled:opacity-60"
        >
          {checking ? "Ellenőrzés..." : "Már megerősítettem →"}
        </button>
        <button onClick={signOut} className="text-xs text-gray-400 hover:text-gray-600">
          Kijelentkezés
        </button>
      </div>
    </div>
  );
}

function AppShell() {
  const { user, loading } = useAuth();
  const [onboardingDone, setOnboardingDone] = useState<boolean | null>(null);
  const [aiEnabled, setAiEnabled] = useState(true);

  // ── JOGI KAPU (ClickUp 123qmzqzk1r §7) ───────────────────────────────
  //
  // ⚠️ getDoc MOUNT-NÁL, NEM onSnapshot (§7.3/b). Egy élő listener a
  // config-emelés pillanatában NAPLÓZÁS KÖZEPÉN dobná fel a blokkoló
  // ablakot — a kapu az app-indítás kérdése, nem valós idejű állapot. Az
  // AppShell minden indításnál újra kiértékel, ez elég.
  const [legalConfig, setLegalConfig] = useState<LegalConfig>(null);
  const [legalAcks, setLegalAcks] = useState<Record<string, boolean>>({});
  const [legalLoaded, setLegalLoaded] = useState(false);
  const [legalRejected, setLegalRejected] = useState(false);
  const legalToday = localDateString(new Date());
  const legalNavigate = useNavigate();
  const legalLocation = useLocation();

  // Firebase Auth action-link (?mode=...&oobCode=...) egységes feloldása —
  // csak induláskor olvassuk, a state referencia stabil marad a komponens
  // életciklusán át (lásd src/utils/authActionMode.ts + teszt).
  const [actionLink] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const oobCode = params.get('oobCode');
    return { mode: resolveAuthActionMode(params.get('mode'), oobCode), oobCode };
  });

  // verifyEmail ág — viselkedés változatlan, csak a mode-feloldás közös
  useEffect(() => {
    if (actionLink.mode === 'verifyEmail' && actionLink.oobCode) {
      import('firebase/auth').then(({ applyActionCode }) => {
        import('./services/firebase').then(({ auth }) => {
          applyActionCode(auth, actionLink.oobCode!)
            .then(() => {
              if (auth.currentUser) auth.currentUser.reload();
              window.location.href = '/fooldal';
            })
            .catch(() => {
              window.location.href = '/fooldal';
            });
        });
      });
    }
  }, [actionLink]);

  // Valós idejű előfizetés a profilra: onboardingDone-hoz és az aiEnabled
  // kapcsolóhoz is — utóbbinak azonnal, oldalújratöltés nélkül kell
  // érvényesülnie, amint a Profil oldalon a felhasználó átállítja.
  useEffect(() => {
    // Reset minden user-váltásnál (logout, fiókváltás) — enélkül egy
    // korábbi munkamenetből maradt onboardingDone/aiEnabled érték stale
    // maradhatna az új user friss snapshotjának beérkezéséig, és a lenti
    // loading-guard (onboardingDone === null) nem kapná el az átmenetet.
    setOnboardingDone(null);
    if (!user) return;
    const unsubscribe = onSnapshot(
      doc(db, "profiles", user.uid),
      (snap) => {
        setOnboardingDone(snap.exists() && snap.data()?.onboardingDone === true);
        setAiEnabled(snap.exists() ? (snap.data()?.aiEnabled ?? true) : true);
      },
      () => {
        setOnboardingDone(true); // hiba esetén ne blokkoljuk
      }
    );
    return unsubscribe;
  }, [user]);

  // A kapu-config és a felhasználó nyugtáinak betöltése.
  //
  // ⚠️ FAIL-OPEN MINDEN HIBA-ÁGON (§7.3/a). Precedens ugyanebben a
  // kapu-létrában: a profil-olvasás hibaágán `setOnboardingDone(true)`.
  // Egy config-olvasási hiba miatt senkit nem zárunk ki az appból — az
  // audit-veszteség késés, nem adatvesztés. A `legalLoaded` jelzi, hogy a
  // kérdés EL VAN DÖNTVE (akár hibával), hogy a kapu ne villanjon fel
  // utólag egy már megnyitott képernyőre.
  useEffect(() => {
    setLegalConfig(null);
    setLegalAcks({});
    setLegalLoaded(false);
    setLegalRejected(false);
    if (!user) return;
    let elhagyott = false;

    (async () => {
      try {
        const config = await loadLegalConfig();
        if (elhagyott) return;
        const acks = await loadAcks(user.uid, config);
        if (elhagyott) return;
        setLegalConfig(config);
        setLegalAcks(acks);
      } catch {
        setLegalConfig(null);          // fail-open
      } finally {
        if (!elhagyott) setLegalLoaded(true);
      }
    })();

    return () => {
      elhagyott = true;
    };
  }, [user]);

  /** Nyugta írása — a modál „Tovább" gombja hívja.
   *
   * ⚠️ A payload a KANONIKUS `buildAckPayload`-ból jön, mert a következő
   * körben a regisztráció is ugyanezt írja (§7.3/d). Egy mezőnyi eltérés azt
   * jelentené, hogy a kapu nem találja meg a saját nyugtáját. */
  async function writeLegalAcks(items: LegalGateItem[]) {
    if (!user) return;
    await writeAcks(user.uid, items);
    setLegalAcks((p) => {
      const n = { ...p };
      for (const i of items) n[ackKey(i.docType, i.version)] = true;
      return n;
    });
    setLegalRejected(false);
  }

  // resetPassword ág — dedikált "Új jelszó megadása" képernyő, auth-állapottól
  // (bejelentkezve/kijelentkezve) függetlenül, a loading-gate ELŐTT.
  if (actionLink.mode === 'resetPassword' && actionLink.oobCode) {
    return <ResetPasswordConfirm oobCode={actionLink.oobCode} />;
  }

  if (shouldShowAuthLoadingSpinner(loading, !!user, onboardingDone)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) return <AuthPage />;

  // Email verifikáció ellenőrzés — Google usereknek nem kell
  if (!user.emailVerified && user.providerData[0]?.providerId === "password") {
    return <EmailVerificationScreen email={user.email || ""} />;
  }

  if (!onboardingDone) {
    return <Onboarding onComplete={() => setOnboardingDone(true)} />;
  }

  // ── JOGI KAPU — a létra utolsó foka, az ONBOARDING UTÁN ──────────────
  //
  // Miért az onboarding után: aki még nem végzett az onboardinggal, annál a
  // jogi nyugtázás értelmezhetetlen sorrendet adna — és a regisztrációnál
  // amúgy is megszületik a nyugta (§7.3/d, következő kör).
  //
  // ⚠️ Amíg a config-olvasás EL NEM DŐLT (`legalLoaded`), nem kapuzunk. Nem
  // spinnert mutatunk: az app indul, és ha kapu kell, az a következő
  // renderben jön. Egy villanó spinner minden indításnál rosszabb, mint egy
  // fél másodperccel később megjelenő modál.
  const { gate: legalGate, banner: legalBannerItems } = legalLoaded
    ? resolveLegalGate(legalConfig, legalAcks, legalToday)
    : { gate: [] as LegalGateItem[], banner: [] };

  // ⚠️ A /profil az EGYETLEN whitelistelt útvonal a kapu mögött (§8): az
  // elutasítási képernyő „Adataim exportálása" és „Fiókom törlése" gombja
  // ODA NAVIGÁL, nem újraimplementálja a logikát (az a Profil.tsx-ben él
  // beágyazva — duplikálni a 10. szabályt sértené).
  const legalGateAktiv = legalGate.length > 0 && legalLocation.pathname !== "/profil";

  if (legalGateAktiv) {
    if (legalRejected) {
      return (
        <LegalRejectedScreen
          effectiveFrom={legalGate[0].effectiveFrom}
          onProfil={() => legalNavigate("/profil")}
          onLogout={() => auth.signOut()}
          onBack={() => setLegalRejected(false)}
        />
      );
    }
    return (
      <LegalGateModal
        items={legalGate}
        onAccept={writeLegalAcks}
        onReject={() => setLegalRejected(true)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <RouteAnalytics />
      {legalBannerItems.length > 0 && (
        <LegalBanner items={legalBannerItems} today={legalToday} />
      )}
      <div className="flex-grow" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 80px)' }}>
        <Routes>
          <Route path="/" element={<Navigate to="/fooldal" replace />} />
          <Route path="/fooldal" element={<Fooldal />} />
          <Route path="/naplo" element={<Naplo />} />
          <Route path="/statisztika" element={<Statisztika />} />
          <Route path="/eszkozok" element={<Eszkozok />} />
          <Route path="/profil" element={<Profil />} />
          <Route path="*" element={<Navigate to="/fooldal" replace />} />
        </Routes>
      </div>

      <nav className="bg-white border-t border-gray-200 flex justify-around pt-2 pb-safe fixed bottom-0 left-0 right-0 shadow-sm z-50" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 8px)' }}>
        {[
          { to: "/fooldal", icon: Home, label: "Főoldal" },
          { to: "/naplo", icon: BookOpen, label: "Napló" },
          { to: "/statisztika", icon: BarChart2, label: "Statisztika" },
          { to: "/eszkozok", icon: Wrench, label: "Eszközök" },
          { to: "/profil", icon: User, label: "Profil" },
        ].map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex flex-col items-center text-xs gap-0.5 px-2 py-1 rounded-lg transition-colors ${
                isActive ? "text-emerald-700 font-semibold" : "text-gray-400 hover:text-gray-600"
              }`
            }
          >
            <Icon size={20} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      {aiEnabled ? (
        <AiChat />
      ) : (
        <div
          className="fixed right-4 max-w-[220px] bg-gray-100 text-gray-500 text-xs leading-snug rounded-2xl px-3 py-2.5 shadow-sm border border-gray-200 z-50"
          style={{ bottom: 'calc(env(safe-area-inset-bottom) + 90px)' }}
        >
          Az AI asszisztens ki van kapcsolva. A Profil oldalon bármikor visszakapcsolhatod.
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/signup" element={<AuthPage defaultMode="register" />} />
          <Route path="*" element={<AppShell />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
